import { useState } from 'react';
import { Book, Code, MessageSquare, Send, Settings, Users, ChevronRight, Search } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';

interface HelpSection {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  content: React.ReactNode;
}

export default function Help() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchTerm, setSearchTerm] = useState('');

  const helpSections: HelpSection[] = [
    {
      id: 'getting-started',
      title: 'Primeros Pasos',
      description: 'Aprende los conceptos básicos para comenzar a usar Validea.',
      icon: Book,
      content: (
        <div className="space-y-6">
          <div className="border-b border-slate-200 pb-6">
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Bienvenido a Validea</h3>
            <p className="text-lg text-slate-600 leading-relaxed">
              Validea es una plataforma que te permite validar tus ideas de negocio a través de MVPs (Productos Mínimos Viables).
              Con Validea, puedes:
            </p>
            <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: 'Gestión de MVPs',
                  description: 'Crea y administra múltiples MVPs desde un solo lugar'
                },
                {
                  title: 'Análisis de Datos',
                  description: 'Recolecta y analiza datos de visitas y contactos'
                },
                {
                  title: 'Toma de Decisiones',
                  description: 'Toma decisiones basadas en datos reales y métricas'
                },
                {
                  title: 'Seguimiento',
                  description: 'Monitorea el interés y la evolución de tus ideas'
                }
              ].map((item, index) => (
                <div key={index} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-2">{item.title}</h4>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg font-medium text-slate-900 mb-4">Pasos para Comenzar</h4>
            <div className="space-y-4">
              {[
                {
                  step: '1',
                  title: 'Crea tu Primer MVP',
                  description: 'Define el nombre, descripción y categoría de tu MVP'
                },
                {
                  step: '2',
                  title: 'Configura el Tracking',
                  description: 'Integra el código de seguimiento en tu sitio web'
                },
                {
                  step: '3',
                  title: 'Personaliza los Formularios',
                  description: 'Implementa los formularios de contacto'
                },
                {
                  step: '4',
                  title: 'Analiza los Resultados',
                  description: 'Revisa las métricas y toma decisiones'
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-lg border border-slate-200">
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-semibold">
                    {item.step}
                  </div>
                  <div>
                    <h5 className="font-medium text-slate-900">{item.title}</h5>
                    <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'tracking-visits',
      title: 'Registro de Visitas',
      description: 'Aprende a integrar el tracking de visitas en tu MVP.',
      icon: Users,
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">Cómo Registrar Visitas</h3>
          <p className="text-slate-600">
            Para registrar visitas a tu MVP, necesitas integrar nuestro endpoint de tracking en tu sitio web o aplicación.
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="text-sm font-medium text-slate-900 mb-2">Endpoint de Visitas</h4>
            <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm overflow-x-auto">
              POST https://api.validea.dev/visits
            </code>
            <p className="mt-2 text-sm text-slate-600">Headers requeridos:</p>
            <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm mt-1 overflow-x-auto">
              {`{
  "Content-Type": "application/json",
  "X-MVP-ID": "tu-mvp-id"
}`}
            </code>
            <p className="mt-2 text-sm text-slate-600">Cuerpo de la solicitud:</p>
            <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm mt-1 overflow-x-auto">
              {`{
  "source": "landing-page",
  "referrer": "https://google.com",
  "userAgent": "Mozilla/5.0...",
  "metadata": {
    "page": "/pricing",
    "campaign": "google-ads"
  }
}`}
            </code>
          </div>
          <p className="text-slate-600">
            Ejemplo de integración con JavaScript:
          </p>
          <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm overflow-x-auto">
            {`async function trackVisit() {
  try {
    const response = await fetch('https://api.validea.dev/visits', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-MVP-ID': 'tu-mvp-id'
      },
      body: JSON.stringify({
        source: window.location.hostname,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        metadata: {
          page: window.location.pathname
        }
      })
    });
    const data = await response.json();
    console.log('Visita registrada:', data);
  } catch (error) {
    console.error('Error al registrar visita:', error);
  }
}`}
          </code>
        </div>
      ),
    },
    {
      id: 'contact-forms',
      title: 'Formularios de Contacto',
      description: 'Implementa formularios para capturar leads interesados.',
      icon: MessageSquare,
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">Integración de Formularios de Contacto</h3>
          <p className="text-slate-600">
            Los formularios de contacto te permiten capturar leads interesados en tu MVP. Integra nuestro endpoint de contacto
            en tus formularios.
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="text-sm font-medium text-slate-900 mb-2">Endpoint de Contacto</h4>
            <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm overflow-x-auto">
              POST https://api.validea.dev/contacts
            </code>
            <p className="mt-2 text-sm text-slate-600">Headers requeridos:</p>
            <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm mt-1 overflow-x-auto">
              {`{
  "Content-Type": "application/json",
  "X-MVP-ID": "tu-mvp-id"
}`}
            </code>
            <p className="mt-2 text-sm text-slate-600">Cuerpo de la solicitud:</p>
            <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm mt-1 overflow-x-auto">
              {`{
  "name": "Juan Pérez",
  "email": "juan@ejemplo.com",
  "phone": "+1234567890",
  "message": "Me interesa saber más",
  "metadata": {
    "plan": "premium",
    "source": "pricing-page"
  }
}`}
            </code>
          </div>
          <p className="text-slate-600">
            Ejemplo de integración con un formulario HTML:
          </p>
          <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm overflow-x-auto">
            {`<form id="contactForm">
  <input type="text" name="name" required>
  <input type="email" name="email" required>
  <input type="tel" name="phone">
  <textarea name="message" required></textarea>
  <button type="submit">Enviar</button>
</form>

<script>
document.getElementById('contactForm').addEventListener('submit', async (e) => {
  e.preventDefault();
  const formData = new FormData(e.target);
  
  try {
    const response = await fetch('https://api.validea.dev/contacts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-MVP-ID': 'tu-mvp-id'
      },
      body: JSON.stringify({
        name: formData.get('name'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        message: formData.get('message'),
        metadata: {
          source: window.location.pathname
        }
      })
    });
    const data = await response.json();
    console.log('Contacto registrado:', data);
    alert('¡Gracias por tu interés!');
  } catch (error) {
    console.error('Error al enviar formulario:', error);
    alert('Hubo un error al enviar el formulario');
  }
});
</script>`}
          </code>
        </div>
      ),
    },
    {
      id: 'api-integration',
      title: 'Integración API',
      description: 'Documentación completa de nuestra API RESTful.',
      icon: Code,
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">Documentación de la API</h3>
          <p className="text-slate-600">
            Nuestra API RESTful te permite integrar Validea con cualquier plataforma o lenguaje de programación.
          </p>
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-slate-900 mb-2">Autenticación</h4>
              <p className="text-sm text-slate-600 mb-2">
                Todas las solicitudes deben incluir tu MVP ID en el header X-MVP-ID.
              </p>
              <code className="block bg-slate-800 text-slate-50 p-3 rounded text-sm overflow-x-auto">
                X-MVP-ID: tu-mvp-id
              </code>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-900 mb-2">Rate Limiting</h4>
              <p className="text-sm text-slate-600">
                Límite de 1000 solicitudes por hora por MVP ID. Los headers de respuesta incluyen:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 ml-4 mt-2">
                <li>X-RateLimit-Limit: límite total</li>
                <li>X-RateLimit-Remaining: solicitudes restantes</li>
                <li>X-RateLimit-Reset: tiempo hasta reset del límite</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'notifications',
      title: 'Notificaciones',
      description: 'Configura las notificaciones para tu MVP.',
      icon: Send,
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">Configuración de Notificaciones</h3>
          <p className="text-slate-600">
            Recibe notificaciones por email cuando recibas nuevas visitas o contactos en tu MVP.
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="text-sm font-medium text-slate-900 mb-2">Tipos de Notificaciones</h4>
            <ul className="list-disc list-inside space-y-2 text-sm text-slate-600 ml-4">
              <li>Resumen diario de visitas</li>
              <li>Notificación inmediata de nuevos contactos</li>
              <li>Reportes semanales de conversión</li>
              <li>Alertas de objetivos alcanzados</li>
            </ul>
          </div>
          <p className="text-slate-600">
            Configura las notificaciones en la sección de ajustes de tu MVP o al momento de crear uno nuevo.
          </p>
        </div>
      ),
    },
    {
      id: 'settings',
      title: 'Configuración',
      description: 'Personaliza los ajustes de tu MVP.',
      icon: Settings,
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">Ajustes del MVP</h3>
          <p className="text-slate-600">
            Personaliza la configuración de tu MVP para obtener mejores resultados.
          </p>
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-slate-900 mb-2">Configuración General</h4>
              <ul className="list-disc list-inside space-y-2 text-sm text-slate-600 ml-4">
                <li>Nombre y descripción del MVP</li>
                <li>URL de origen</li>
                <li>Categoría y etiquetas</li>
                <li>Estado del MVP</li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-900 mb-2">Configuración de Seguimiento</h4>
              <ul className="list-disc list-inside space-y-2 text-sm text-slate-600 ml-4">
                <li>Fuentes de tráfico personalizadas</li>
                <li>Parámetros UTM</li>
                <li>Eventos personalizados</li>
                <li>Objetivos de conversión</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const filteredSections = helpSections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Centro de Ayuda</h1>
          <p className="text-lg text-slate-600">
            Encuentra toda la información necesaria para sacar el máximo provecho de Validea.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar de navegación */}
          <div className="md:w-80 flex-shrink-0 space-y-4">
            {/* Buscador */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar ayuda..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Menú de navegación */}
            <nav className="bg-white rounded-lg border border-slate-200 overflow-hidden sticky top-4">
              <div className="p-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">
                  Temas de Ayuda
                </h2>
              </div>
              <div className="p-2">
                {filteredSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg transition-all ${
                        activeSection === section.id
                          ? 'bg-primary-50 text-primary-700 shadow-sm'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${
                        activeSection === section.id
                          ? 'text-primary-600'
                          : 'text-slate-400'
                      }`} />
                      <div className="text-left">
                        <div className="font-medium">{section.title}</div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                          {section.description}
                        </p>
                      </div>
                      <ChevronRight
                        className={`w-4 h-4 ml-auto flex-shrink-0 transition-transform ${
                          activeSection === section.id ? 'rotate-90 text-primary-600' : 'text-slate-400'
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Contenido principal */}
          <div className="flex-1">
            <div className="bg-white rounded-lg border border-slate-200 p-8">
              {helpSections.find((section) => section.id === activeSection)?.content}
            </div>

            {/* Sección de ayuda adicional */}
            <div className="mt-8 bg-primary-50 rounded-lg border border-primary-100 p-6">
              <h3 className="text-lg font-semibold text-primary-900 mb-2">
                ¿Necesitas más ayuda?
              </h3>
              <p className="text-primary-700 mb-4">
                Si no encuentras la respuesta que buscas, nuestro equipo de soporte está aquí para ayudarte.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="mailto:validea@alineatec.com"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-primary-700 rounded-lg border border-primary-200 hover:bg-primary-50 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  Contactar Soporte
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
} 